/**
 * 产品配置JSON读取工具
 * 提供统一的产品数据读取接口
 */

import productsData from './products.json';

/**
 * 获取所有产品分类
 * @returns {Object} 所有分类信息
 */
export const getProductCategories = () => {
  return productsData.categories;
};

/**
 * 获取所有产品数据
 * @returns {Object} 所有产品信息
 */
export const getAllProducts = () => {
  return productsData.products;
};

/**
 * 根据产品ID获取产品详情
 * @param {string} productId - 产品ID
 * @returns {Object|null} 产品详情对象
 */
export const getProductById = (productId) => {
  return productsData.products[productId] || null;
};

/**
 * 根据分类获取产品列表 (支持子分类)
 * @param {string} categoryId - 分类ID ('all' 返回所有产品)
 * @returns {Array} 产品列表
 */
export const getProductsByCategory = (categoryId) => {
  if (categoryId === 'all') {
    return Object.values(productsData.products);
  }
  
  // 检查是否为子分类
  if (categoryId.includes('_')) {
    const [parentCategory, subCategory] = categoryId.split('_');
    return Object.values(productsData.products).filter(
      product => product.category === parentCategory && product.subCategory === subCategory
    );
  }
  
  // 如果是父分类，返回该分类下的所有产品（包括子分类产品）
  return Object.values(productsData.products).filter(
    product => product.category === categoryId
  );
};

/**
 * 获取产品的所有版本（展开成单独的卡片）
 * @param {string} productId - 产品ID
 * @returns {Array} 产品版本列表
 */
export const getProductVersions = (productId) => {
  const product = getProductById(productId);
  if (!product) return [];

  return product.versions.map(version => ({
    ...version,
    productName: product.name,
    productCategoryId: product.id,
    versionProductId: version.productId,
    productDescription: product.description,
    highlights: product.highlights,
    type: product.type || 'web',
    productImage: product.image,  // 添加产品图片
    priceRMB: version.priceRMB,  // 添加人民币价格
    diamondReward: version.diamondReward || product.diamondReward || 0,  // 优先使用版本级别的奖励，再使用产品级别的
    isGift: product.isGift || false,  // 是否为赠品
    giftFrom: product.giftFrom || null,  // 赠品来源产品ID
    giftLabel: product.giftLabel || null  // 赠品标签
  }));
};

/**
 * 获取所有产品的所有版本（展开成卡片列表）
 * @returns {Array} 所有产品版本卡片列表
 */
export const getAllProductCards = () => {
  const cards = [];
  Object.values(productsData.products).forEach(product => {
    product.versions.forEach(version => {
      cards.push({
        ...version,
        productName: product.name,
        productCategoryId: product.id,
        versionProductId: version.productId,
        productDescription: product.description,
        highlights: product.highlights,
        type: product.type || 'web',
        productImage: product.image,  // 添加产品图片
        priceRMB: version.priceRMB,  // 添加人民币价格
        diamondReward: version.diamondReward || product.diamondReward || 0,  // 优先使用版本级别的奖励，再使用产品级别的
        isGift: product.isGift || false,  // 是否为赠品
        giftFrom: product.giftFrom || null,  // 赠品来源产品ID
        giftLabel: product.giftLabel || null  // 赠品标签
      });
    });
  });
  return cards;
};

/**
 * 根据分类筛选产品卡片 (支持子分类)
 * @param {string} categoryId - 分类ID
 * @returns {Array} 筛选后的产品卡片列表
 */
export const getFilteredProductCards = (categoryId) => {
  const allCards = getAllProductCards();
  
  if (categoryId === 'all') {
    return allCards;
  }
  
  // 检查是否为子分类
  if (categoryId.includes('_')) {
    const [parentCategory, subCategory] = categoryId.split('_');
    return allCards.filter(card => 
      card.productCategoryId.includes(parentCategory) && 
      card.productCategoryId.includes(subCategory)
    );
  }
  
  // 如果是父分类，返回该分类下的所有卡片（包括子分类）
  return allCards.filter(card => card.productCategoryId.includes(categoryId));
};

/**
 * 获取产品的特点标签
 * @param {string} productId - 产品ID
 * @returns {Array} 特点标签数组
 */
export const getProductHighlights = (productId) => {
  const product = getProductById(productId);
  return product ? product.highlights : [];
};

/**
 * 获取产品的功能特性
 * @param {string} productId - 产品ID
 * @returns {Array} 功能特性数组
 */
export const getProductFeatures = (productId) => {
  const product = getProductById(productId);
  return product ? product.features : [];
};

/**
 * 获取产品的技术规格
 * @param {string} productId - 产品ID
 * @returns {Array} 技术规格数组
 */
export const getProductSpecs = (productId) => {
  const product = getProductById(productId);
  return product ? product.specs : [];
};

/**
 * 获取产品的FAQ
 * @param {string} productId - 产品ID
 * @returns {Array} FAQ数组
 */
export const getProductFAQ = (productId) => {
  const product = getProductById(productId);
  return product ? product.faq : [];
};

/**
 * 获取分类的样式主题
 * @param {string} categoryId - 分类ID
 * @returns {Object} 主题样式对象
 */
export const getCategoryTheme = (categoryId) => {
  const themes = {
    // AIMonkey 会员 - 蓝色主题
    aimonkey_member: {
      gradient: 'from-blue-50 via-white to-sky-50',
      border: 'border-blue-200',
      accent: 'text-blue-600',
      button: 'from-blue-500 to-blue-600',
      tag: 'bg-blue-100 text-blue-700'
    },
    // AIMonkey VIP大会员 - 紫色主题
    aimonkey_vip: {
      gradient: 'from-purple-50 via-white to-violet-50',
      border: 'border-purple-200',
      accent: 'text-purple-600',
      button: 'from-purple-500 to-purple-600',
      tag: 'bg-purple-100 text-purple-700'
    },
    // 钻石服务 - 粉色主题
    diamond_service: {
      gradient: 'from-pink-50 via-white to-rose-50',
      border: 'border-pink-200',
      accent: 'text-pink-600',
      button: 'from-pink-500 to-pink-600',
      tag: 'bg-pink-100 text-pink-700'
    },
    // 官网账户/带充值 - 绿色主题
    diamond_official_recharge: {
      gradient: 'from-emerald-50 via-white to-green-50',
      border: 'border-green-200',
      accent: 'text-green-600',
      button: 'from-green-500 to-green-600',
      tag: 'bg-green-100 text-green-700'
    },
    // Gemini测试天卡 - 绿色主题（参考AI车队Gemini配色）
    testing: {
      gradient: 'from-green-50 via-emerald-50 to-teal-50',
      border: 'border-green-300',
      accent: 'bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent',
      button: 'from-green-500 to-emerald-600',
      tag: 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
    },
    // GPT Plus车队单卡 - 灰蓝色主题
    fleet_gpt_plus: {
      gradient: 'from-gray-50 via-slate-50 to-blue-50',
      border: 'border-gray-300',
      accent: 'text-gray-700',
      button: 'from-gray-500 to-gray-600',
      tag: 'bg-gray-100 text-gray-700'
    },
    // Sora车队单卡 - 蓝色主题
    fleet_sora: {
      gradient: 'from-blue-50 via-sky-50 to-cyan-50',
      border: 'border-blue-300',
      accent: 'text-blue-600',
      button: 'from-blue-500 to-blue-600',
      tag: 'bg-blue-100 text-blue-700'
    },
    // Claude车队单卡 - 紫色主题
    fleet_claude: {
      gradient: 'from-purple-50 via-violet-50 to-indigo-50',
      border: 'border-purple-300',
      accent: 'text-purple-600',
      button: 'from-purple-500 to-purple-600',
      tag: 'bg-purple-100 text-purple-700'
    },
    // Grok车队单卡 - 橙色主题
    fleet_grok: {
      gradient: 'from-orange-50 via-amber-50 to-yellow-50',
      border: 'border-orange-300',
      accent: 'text-orange-600',
      button: 'from-orange-500 to-orange-600',
      tag: 'bg-orange-100 text-orange-700'
    },
    // Gemini车队 - 绿色主题
    fleet_gemini: {
      gradient: 'from-green-50 via-emerald-50 to-teal-50',
      border: 'border-green-300',
      accent: 'text-green-600',
      button: 'from-green-500 to-emerald-600',
      tag: 'bg-green-100 text-green-700'
    },
    // Claude普号车队 - 浅紫色主题
    fleet_claude_normal: {
      gradient: 'from-indigo-50 via-purple-50 to-violet-50',
      border: 'border-indigo-300',
      accent: 'text-indigo-600',
      button: 'from-indigo-500 to-indigo-600',
      tag: 'bg-indigo-100 text-indigo-700'
    },
    // 兼容旧的分类ID
    aimonkey: {
      gradient: 'from-blue-50 via-white to-sky-50',
      border: 'border-blue-200',
      accent: 'text-blue-600',
      button: 'from-blue-500 to-blue-600',
      tag: 'bg-blue-100 text-blue-700'
    },
    diamond: {
      gradient: 'from-pink-50 via-white to-rose-50',
      border: 'border-pink-200',
      accent: 'text-pink-600',
      button: 'from-pink-500 to-pink-600',
      tag: 'bg-pink-100 text-pink-700'
    }
  };
  
  return themes[categoryId] || {
    gradient: 'from-orange-50 via-white to-amber-50',
    border: 'border-orange-200',
    accent: 'text-orange-600',
    button: 'from-orange-500 to-orange-600',
    tag: 'bg-orange-100 text-orange-700'
  };
};

/**
 * 获取配置文件元数据
 * @returns {Object} 元数据信息
 */
export const getMetadata = () => {
  return productsData.metadata;
};

/**
 * 获取扁平化的分类列表（包含子分类）
 * @returns {Array} 扁平化的分类列表
 */
export const getFlatCategories = () => {
  const categories = [];
  
  Object.values(productsData.categories).forEach(category => {
    if (category.hasSubCategories) {
      // 添加父分类
      categories.push({
        ...category,
        displayName: category.name,
        isParent: true
      });
      
      // 添加子分类
      Object.values(category.subCategories).forEach(subCategory => {
        categories.push({
          ...subCategory,
          displayName: `${category.name} - ${subCategory.name}`,
          parentId: category.id,
          isSubCategory: true
        });
      });
    } else {
      categories.push({
        ...category,
        displayName: category.name,
        isParent: false
      });
    }
  });
  
  return categories;
};

/**
 * 获取分类的显示名称
 * @param {string} categoryId - 分类ID
 * @returns {string} 显示名称
 */
export const getCategoryDisplayName = (categoryId) => {
  if (categoryId === 'all') {
    return '全部';
  }
  
  // 检查是否为子分类
  if (categoryId.includes('_')) {
    const [parentId, subId] = categoryId.split('_');
    const parentCategory = productsData.categories[parentId];
    if (parentCategory && parentCategory.subCategories) {
      const subCategory = parentCategory.subCategories[subId];
      if (subCategory) {
        return `${parentCategory.name} - ${subCategory.name}`;
      }
    }
  }
  
  // 父分类
  const category = productsData.categories[categoryId];
  return category ? category.name : categoryId;
};